import { useState } from "react";
import { defectAPI } from "../utils/api";

const SAMPLE_DEFECTS = [
  {
    id: "honeycomb",
    title: "Concrete Honeycombing",
    subtitle: "Gaps & Exposed Rebar in Beam/Column",
    icon: "🧱",
    type: "honeycomb",
    desc: "Aggregate compaction issue during RCC column casting causing visible voids, holes, and honeycomb gaps."
  },
  {
    id: "crack",
    title: "Diagonal Wall Crack",
    subtitle: "Near Window Frame & Lintel Joint",
    icon: "⚡",
    type: "crack_diagonal",
    desc: "45-degree diagonal crack originating from window frame corners along brick wall lintel joints."
  },
  {
    id: "dampness",
    title: "Efflorescence & Plaster Dampness",
    subtitle: "Shor / Water Seepage on Internal Wall",
    icon: "💧",
    type: "dampness",
    desc: "Ground-level walls showing moisture absorption, peeling plaster, and white salt powder deposits (efflorescence)."
  },
  {
    id: "corrosion",
    title: "Rebar Rusting & Concrete Spalling",
    subtitle: "Roof Slab Concrete Peeling Off",
    icon: "🔩",
    type: "corrosion",
    desc: "Slab plaster spalling off with corroded, rusted TMT steel rebar exposed to moisture."
  }
];

export default function DefectScannerPage({ onAskAI }) {
  const [selectedType, setSelectedType] = useState("honeycomb");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  const handleAudit = async (typeToUse = selectedType) => {
    setLoading(true);
    setError("");
    try {
      const res = await defectAPI.audit({
        defect_type: typeToUse,
        description: description || "Defect reported from site inspection."
      });
      if (res.success) {
        setReport(res.data.diagnosis);
      }
    } catch (err) {
      setError(err.message || "Audit failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSampleClick = (sample) => {
    setSelectedType(sample.type);
    setDescription(sample.desc);
    handleAudit(sample.type);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">📸 AI Site Defect & Crack Inspector</h1>
          <p className="page-subtitle">
            Diagnose concrete honeycombing, shear cracks, efflorescence dampness & rebar corrosion with IS-code repair protocols.
          </p>
        </div>
        <div className="badge-pill">IS-Code Engineering Audit</div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Preset Defect Cards */}
      <h3 style={{ marginBottom: "14px", color: "var(--charcoal)" }}>Select a Common Civil Defect or Input Details:</h3>
      <div className="features-grid" style={{ marginBottom: "24px" }}>
        {SAMPLE_DEFECTS.map((sample) => {
          const isSelected = selectedType === sample.type;
          return (
            <div
              key={sample.id}
              onClick={() => handleSampleClick(sample)}
              className="card feature-card"
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: isSelected ? "2px solid var(--saffron)" : "1px solid var(--border)",
                background: isSelected ? "var(--saffron-light)" : "var(--white)",
                boxShadow: isSelected ? "var(--shadow-md)" : "var(--shadow-sm)"
              }}
            >
              <div style={{ fontSize: "28px", marginBottom: "8px" }}>{sample.icon}</div>
              <h4 style={{ margin: "0 0 4px 0", color: "var(--charcoal)", fontSize: "16px", fontWeight: "700" }}>{sample.title}</h4>
              <div style={{ fontSize: "12px", color: "var(--saffron)", fontWeight: "600", marginBottom: "8px" }}>{sample.subtitle}</div>
              <p style={{ fontSize: "13px", color: "var(--slate)", margin: 0, lineHeight: "1.5" }}>{sample.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Manual Input Form */}
      <div className="card form-card" style={{ marginBottom: "24px" }}>
        <h3 className="card-title">Custom Site Defect Description</h3>
        <div className="form-group">
          <label>Defect Category</label>
          <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="select-input">
            <option value="honeycomb">Concrete Honeycombing (Gaps in Beam/Column)</option>
            <option value="crack_diagonal">Diagonal Wall Crack (Window / Shear Joint)</option>
            <option value="dampness">Wall Dampness / Shor / Efflorescence</option>
            <option value="corrosion">TMT Steel Rusting & Concrete Spalling</option>
          </select>
        </div>

        <div className="form-group">
          <label>Site Description / Observations</label>
          <textarea
            rows="3"
            placeholder="Describe location, crack width, age of building, or water seepage source..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="textarea-input"
          ></textarea>
        </div>

        <button onClick={() => handleAudit(selectedType)} disabled={loading} className="btn btn-primary btn-full">
          {loading ? "Analyzing IS-Code Remediation..." : "🔍 Run AI Engineering Diagnosis"}
        </button>
      </div>

      {/* Diagnostic Result Card */}
      {report && (
        <div className="card" style={{ padding: "28px", borderLeft: "5px solid var(--saffron)", background: "var(--white)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <div style={{ display: "inline-block", background: "var(--red-light)", color: "var(--red)", border: "1px solid rgba(239, 68, 68, 0.2)", padding: "4px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "bold", marginBottom: "8px" }}>
                SEVERITY: {report.severity}
              </div>
              <h2 style={{ margin: 0, color: "var(--charcoal)", fontSize: "22px" }}>{report.title}</h2>
              <div style={{ fontSize: "13px", color: "var(--saffron)", marginTop: "4px", fontWeight: "600" }}>IS-Code Standard: <strong>{report.is_code_ref}</strong></div>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => onAskAI && onAskAI(`Detail repair procedure for ${report.title} as per ${report.is_code_ref}`)}
            >
              💬 Ask AI Specific Chemical Brands
            </button>
          </div>

          <div className="stats-grid" style={{ marginBottom: "20px" }}>
            <div className="stat-box" style={{ background: "var(--saffron-subtle)", border: "1px solid var(--border-light)" }}>
              <div className="stat-label" style={{ color: "var(--slate)" }}>Urgency Level</div>
              <div className="stat-value" style={{ fontSize: "16px", color: "var(--saffron)" }}>{report.urgency}</div>
            </div>
            <div className="stat-box" style={{ background: "var(--saffron-subtle)", border: "1px solid var(--border-light)" }}>
              <div className="stat-label" style={{ color: "var(--slate)" }}>Safety Risk Impact</div>
              <div className="stat-desc" style={{ color: "var(--charcoal)" }}>{report.safety_risk}</div>
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <h4 style={{ color: "var(--charcoal)", marginBottom: "8px", fontSize: "15px" }}>🔍 Root Cause Engineering Analysis:</h4>
            <p style={{ background: "var(--cream)", padding: "16px", borderRadius: "8px", border: "1px solid var(--border)", margin: 0, color: "var(--charcoal)", lineHeight: "1.6" }}>
              {report.root_cause}
            </p>
          </div>

          <div>
            <h4 style={{ color: "var(--green)", marginBottom: "12px", fontSize: "15px" }}>🛠️ IS-Code Compliant Step-by-Step Repair Protocol:</h4>
            <div className="steps-list">
              {report.remediation_steps.map((step, idx) => (
                <div key={idx} style={{ display: "flex", gap: "12px", marginBottom: "10px", background: "var(--cream)", padding: "12px 16px", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <div style={{ background: "var(--green)", color: "white", width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "bold", flexShrink: 0, marginTop: "2px" }}>
                    {idx + 1}
                  </div>
                  <div style={{ color: "var(--charcoal)", fontSize: "14px", lineHeight: "1.5" }}>{step}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
